import { IAssignment, IJob, ILocation, IPayout, ITimecard } from '@medely/types';
import { JobWithJobType } from '@mp';
import moment, { MomentInput, tz } from 'moment-timezone';
import { JobForIsOvernight, isOvernight } from './datetime';
import { pluralize } from 'core/utils/text';
import { JobStatusEnum as JobStatus } from '@medely/web-components/types';
import { MedelyDateTime } from '@medely/date-time-tools';
import { Job, JobTypeList } from '@mp/graphql/generated/graphql';
import MPDisplayHelpers from '@mp/utils/displayHelpers';

const dateTimeLegacy = {
  date: (date: string, tz?: string, format = 'MM/DD/YY') => formatDateTime(date, format, tz),
  time: (date: string, tz?: string) => formatDateTime(date, 'HH:mm', tz),
  custom: (date: string, format: string, tz?: string) => formatDateTime(date, format, tz),
};

const dateTime = {
  date: (date: string, tz?: string, format = 'M/D/YY') => formatDateTime(date, format, tz),
  time: (date: string, tz?: string) => formatDateTime(date, 'HH:mm', tz),
  custom: (date: string, format: string, tz?: string) => formatDateTime(date, format, tz),
};

function jobSpecialties(job: Partial<IJob>) {
  return job.specialties?.map((s) => s.label).join(', ') || '–';
}

function jobPositions(job: Partial<IJob>) {
  return job?.positions?.map((s) => s.display_name).join(', ') || '–';
}

const cancelledStatuses = [JobStatus.Canceled, JobStatus.CanceledWithFee];

function displayTimecardStatus({
  status,
  shift_type,
}: Pick<IJob, 'status' | 'shift_type'>): string {
  if (status === JobStatus.MissedShift) {
    return 'Missed Shift';
  }
  if (cancelledStatuses.includes(status as JobStatus)) {
    return 'Cancelled by Facility';
  }
  if (shift_type === 'on_call') {
    return 'On Call Shift';
  }
  if (shift_type === 'call_back') {
    return 'Call Back Shift';
  }
  // shift_type === 'regular'
  return 'Regular Shift';
}

function timecardCustomDates(timecard: ITimecard) {
  return (
    dateTimeLegacy.custom(dateTimeLegacy.date(timecard.starts_time), 'MMMM DD') +
    ' - ' +
    dateTimeLegacy.custom(dateTimeLegacy.date(timecard.ends_time), 'MMMM DD')
  );
}

function jobFullDayDateLegacy(job: Partial<IJob>) {
  const date = job.starts_date || job.current_starts_time;
  if (!date) {
    return null;
  }
  return dateTimeLegacy.custom(date, 'dddd, MM/DD/YY', job.location?.timezone_lookup);
}

function jobFullDayDate(job: Partial<IJob>) {
  const date = job.starts_date || job.current_starts_time;
  if (!date) {
    return null;
  }
  return dateTimeLegacy.custom(date, 'dddd, M/D/YY', job.location?.timezone_lookup);
}

function jobDisputeScheduledTime(job: Partial<IJob>): string | null {
  const { starts_time, ends_time, location, guaranteed_minimum_hours, status } = job;
  if (!starts_time || !ends_time) {
    return null;
  }

  if (status === 'canceled_with_fee') {
    return ` ${pluralize(guaranteed_minimum_hours ?? 0, 'hr')}`;
  }

  const timezone = location?.timezone_lookup || tz.guess();
  const start = tz(starts_time, timezone);
  const end = tz(ends_time, timezone);
  const hoursDiff = end.diff(start, 'hours', true).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  const hours = Math.max(guaranteed_minimum_hours ?? 0, +hoursDiff);

  return ` ${pluralize(hours, 'hr')}`;
}

function jobTimeLegacy(
  job: Partial<IJob>,
  {
    withShiftLength = true,
    scheduledShiftStyle = false,
  }: { withShiftLength?: boolean; scheduledShiftStyle?: boolean } = {},
): string | null {
  const {
    current_starts_time,
    approved_clock_in,
    current_ends_time,
    approved_clock_out,
    location,
    guaranteed_minimum_hours,
    status,
  } = job;
  if (!current_starts_time || !current_ends_time) {
    return null;
  }

  const timezone = location?.timezone_lookup || tz.guess();

  const startsTime = approved_clock_in || current_starts_time;
  const endsTime = approved_clock_out || current_ends_time;

  const start = new MedelyDateTime(startsTime, {
    tz: timezone,
  });

  const end = new MedelyDateTime(endsTime, {
    tz: timezone,
  });

  const hoursDiff = start.difference(end, 'hours').toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  const hours =
    status === 'canceled_with_fee'
      ? guaranteed_minimum_hours ?? 0
      : Math.max(guaranteed_minimum_hours ?? 0, +hoursDiff);

  return scheduledShiftStyle
    ? `${start.militaryTime} - ${end.militaryTime}${
        withShiftLength ? ` (${pluralize(hours, 'hr')})` : ``
      }`
    : `${start.militaryTime}-${end.militaryTime}${
        withShiftLength ? ` • ${pluralize(hours, 'hr')}` : ``
      }`;
}

function jobTime(
  job: Partial<JobWithJobType>,
  {
    withShiftLength = true,
    scheduledShiftStyle = false,
  }: { withShiftLength?: boolean; scheduledShiftStyle?: boolean } = {},
  currentUser?: { primary_locations?: { timezone_lookup: string }[] },
): string | null {
  const format = 'HH:mm';
  const {
    current_starts_time,
    approved_clock_in,
    current_ends_time,
    approved_clock_out,
    location,
    guaranteed_minimum_hours,
    status,
  } = job;
  if (!current_starts_time || !current_ends_time) {
    return null;
  }

  const timezone = location?.timezone_lookup || tz.guess();
  const startsTime = approved_clock_in || current_starts_time;
  const endsTime = approved_clock_out || current_ends_time;
  const start = tz(startsTime, timezone);
  const end = tz(endsTime, timezone);
  const hoursDiff = end.diff(start, 'hours', true).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  if (job.job_type === 'time_off') {
    const timezone =
      job.assignment?.primary_location?.timezone_lookup ||
      currentUser?.primary_locations?.[0]?.timezone_lookup ||
      tz.guess();

    const start = tz(job.current_starts_time, timezone);
    const end = tz(job.current_ends_time, timezone);

    const startsAtMidnight = start.isSame(tz(start, timezone).startOf('day'), 'minute');
    // Time off shifts have a maximum length of 24 hours
    const endsAtMidnight = end.isSame(tz(end, timezone).startOf('day'), 'minute');

    if (startsAtMidnight && endsAtMidnight) {
      return 'All day';
    }
  }

  const hours =
    status === 'canceled_with_fee'
      ? guaranteed_minimum_hours ?? 0
      : Math.max(guaranteed_minimum_hours ?? 0, +hoursDiff);

  return scheduledShiftStyle
    ? `${start.format(format)} - ${end.format(format)}${
        withShiftLength ? ` (${pluralize(hours, 'hr')})` : ``
      }`
    : `${start.format(format)}-${end.format(format)}${
        withShiftLength ? ` (${pluralize(hours, 'hr')})` : ``
      }`;
}

function jobHours({ current_starts_time, current_ends_time }: IJob | Partial<IJob>): string {
  const start = moment(current_starts_time);
  const end = moment(current_ends_time);

  const hours = end.diff(start, 'hours', true);

  if (hours >= 23.98) {
    return '24 hrs';
  }

  const formattedHours = hours.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return `${formattedHours} hrs`;
}

function assignmentTime(assignment: Partial<IAssignment>): string | null {
  const format = 'HH:mm';
  const { number_of_shifts_per_week, starts_time, ends_time, primary_location } = assignment;
  const timezone = primary_location?.timezone_lookup || tz.guess();
  const start = tz(starts_time, timezone);
  const end = tz(ends_time, timezone);
  // hoursDiff is specifically the raw ends - starts time.
  // Do not confuse this with assignment.hours_per_shift, which deducts estimated break time.
  const hoursDiff = end.diff(start, 'hours', true);

  return `${number_of_shifts_per_week}x${hoursDiff.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}hr Shifts • ${start.format(format)} - ${end.format(format)}`;
}

function assignmentExpectedHours(
  assignment: Pick<IAssignment, 'hours_per_shift' | 'number_of_shifts_per_week'>,
): string {
  // assignment.hours_per_shift deducts estimated break time.
  // This is exactly what we want when displaying expected hours, because its shown next to compensation.
  const { hours_per_shift, number_of_shifts_per_week } = assignment;
  return (hours_per_shift * number_of_shifts_per_week).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function assignmentPositions(assignment: Partial<IAssignment>) {
  return assignment.positions?.map((s) => s.display_name).join(', ') || '–';
}

type AssignmentForFullDayDate = {
  starts_date?: string | null;
  onboarding_due_date?: string | null;
  primary_location?: { timezone_lookup?: string | null } | null;
};

function assignmentFullDayDate(
  assignment: AssignmentForFullDayDate,
  isAssignmentOnboarding = false,
) {
  const date = isAssignmentOnboarding ? assignment.onboarding_due_date : assignment.starts_date;
  if (!date) {
    return null;
  }
  return dateTimeLegacy.custom(
    date,
    'dddd, MM/DD/YY',
    assignment.primary_location?.timezone_lookup ?? undefined,
  );
}

function assignmentDates(
  { starts_date, ends_date }: IAssignment,
  { withParentheses = true, withAbbreviation = true },
): string {
  return `${dateTimeLegacy.date(starts_date)} - ${dateTimeLegacy.date(ends_date)}${
    withParentheses ? ` (` : ` • `
  }${pluralize(weekCount(starts_date, ends_date), withAbbreviation ? `wk` : `Week`)}${
    withParentheses ? `)` : ``
  }`;
}

function assignmentStartDateWithWeekCountLegacy({ starts_date, ends_date }: IAssignment): string {
  return starts_date && ends_date
    ? `${dateTimeLegacy.date(starts_date)} • ${pluralize(
        weekCount(starts_date, ends_date),
        'week',
      )}`
    : ``;
}

function assignmentStartDateWithWeekCount({ starts_date, ends_date }: IAssignment): string {
  return starts_date && ends_date
    ? `${dateTime.date(starts_date)} • ${MPDisplayHelpers.assignment.weekAndDayCount(
        starts_date,
        ends_date,
      )}`
    : ``;
}

function weekCount(starts_date: string, ends_date: string): number {
  const start = moment(starts_date);
  const end = moment(ends_date);
  const weekCount = Math.round((end.diff(start, 'days') + 1) / 7);

  return weekCount;
}

function formatDateTime(dateTime: MomentInput, format: string, timezone?: string): string {
  if (!dateTime) {
    return '';
  }
  if (timezone) {
    return tz(dateTime, timezone).format(format);
  }
  return moment.parseZone(dateTime).format(format);
}

export type JobForShiftType = Pick<IJob, 'shift_type'> & JobForIsOvernight;
function shiftType(job: Job): string | undefined {
  const { shift_type } = job;

  const isOverNight = isOvernight(job);

  if (shift_type === ('regular' as JobTypeList)) {
    return isOverNight ? 'Regular shift overnight' : 'Regular shift';
  }

  if (shift_type === ('on_call' as JobTypeList)) {
    return isOverNight ? 'On call shift overnight' : 'On call shift';
  }

  if (shift_type === ('call_back' as JobTypeList)) {
    return isOverNight ? 'Call back shift overnight' : 'Call back shift';
  }

  if (shift_type === ('missed' as JobTypeList)) {
    return 'Missed shift';
  }
  if (shift_type === ('cancelled' as JobTypeList)) {
    return 'Cancelled shift';
  }

  if (shift_type === ('time_off' as JobTypeList)) {
    return 'Time off';
  }
}

const cityStateZip = (location: ILocation | Partial<ILocation>) => {
  const city = location.address_city;
  if (city) {
    return `${location?.address_city ?? ''}, ${location?.state?.abbreviation ?? ''} ${
      location?.address_zip ?? ''
    }`;
  } else {
    return `${location?.state?.abbreviation ?? ''} ${location?.address_zip ?? ''}`.trim();
  }
};

const cityState = (location: Partial<ILocation>) => {
  const city = location.address_city;
  if (city) {
    return `${location.address_city ?? ''}, ${location.state?.abbreviation ?? ''}`;
  } else {
    return `${location.state?.abbreviation ?? ''}`;
  }
};

type CityZipLocation = { address_city?: string | null; address_zip?: string | null };
const cityZip = (location: Partial<ILocation> | CityZipLocation) => {
  const city = location.address_city;
  const zip = location.address_zip ?? '';

  return !city ? zip : `${city} ${zip}`.trim();
};

const facilityCityState = (location: Partial<ILocation>) => {
  const name = location.name;
  const city = location.address_city;

  if (!name && !city) {
    return `${location.state?.abbreviation ?? ''}`;
  }

  if (!city) {
    return `${location.name ?? ''}, ${location.state?.abbreviation ?? ''}`;
  }

  if (!name) {
    return `${location?.address_city ?? ''}, ${location.state?.abbreviation ?? ''}`;
  }

  return `${location.name ?? ''}, ${location?.address_city ?? ''}, ${
    location.state?.abbreviation ?? ''
  }`;
};

const facilityCity = (location: Partial<ILocation>) => {
  const name = location.name;

  return !name
    ? `${location?.address_city ?? ''}`
    : `${location.name ?? ''}, ${location?.address_city ?? ''}`;
};

const assignmentAddress = (assignment: Partial<IAssignment>, variant: string) => {
  const location = assignment.primary_location;
  if (!location) {
    return undefined;
  }

  const isApplied = variant === 'applications';
  const isBooked = variant === 'booked';
  const isTravel = assignment.is_w2 && assignment.is_travel;
  const showFacilityNameInAddress = isBooked || isApplied;

  if (
    (showFacilityNameInAddress && isTravel && isBooked) ||
    (showFacilityNameInAddress && isApplied)
  ) {
    return `${facilityCityState(location)}`;
  }

  if (showFacilityNameInAddress && !isTravel && isBooked) {
    return `${facilityCity(location)}`;
  }

  if (!showFacilityNameInAddress && isTravel) {
    return `${cityState(location)}`;
  }

  if (!showFacilityNameInAddress && !isTravel) {
    return `${cityZip(location)}`;
  }
};

const IN_TRANSIT_STATUSES = ['in_transit', 'pending'];
const PAID_DISPUTED_STATUSES = ['paid', 'disputed'];
const OPEN_STATUSES = ['open', 'submitted'];
const getPayoutStatus = (payout: IPayout) => {
  if (PAID_DISPUTED_STATUSES.includes(payout.status)) {
    return payout.status;
  }
  if (IN_TRANSIT_STATUSES.includes(payout.status)) {
    return 'in_transit';
  }
  if (OPEN_STATUSES.includes(payout.status)) {
    return 'open';
  } else {
    return 'processing';
  }
};

type SpecialtyItem = {
  label?: string | null;
};
type SpecialtyContainer = {
  specialties?: SpecialtyItem[] | null;
};
const formatSpecialties = (
  item: Partial<IAssignment> | Partial<IJob> | SpecialtyContainer,
  isMobile: boolean,
  maxLabelSize?: number,
): string => {
  const specialtiesCount = item.specialties?.length ?? 0;
  const hasMultipleSpecialties = specialtiesCount > 1;
  const label = item.specialties?.[0].label ?? '';
  const isBigLabel = (label.length ?? 0) > (maxLabelSize ?? Infinity);
  const truncateLabel = isMobile && isBigLabel;

  const specialty = truncateLabel ? `${label.slice(0, maxLabelSize)}...` : label;
  const otherSpecialties = hasMultipleSpecialties ? ` +${specialtiesCount - 1} more` : '';

  return specialty.concat(otherSpecialties);
};

const DisplayHelpers = {
  dateTime: dateTimeLegacy,
  timecardCustomDates,
  job: {
    displayTimecardStatus,
    disputeScheduledTime: jobDisputeScheduledTime,
    fullDayDate: jobFullDayDate,
    fullDayDateLegacy: jobFullDayDateLegacy,
    jobHours,
    positions: jobPositions,
    shiftType,
    specialties: jobSpecialties,
    time: jobTime,
    timeLegacy: jobTimeLegacy,
  },
  assignment: {
    address: assignmentAddress,
    dates: assignmentDates,
    startDateWithWeekCountLegacy: assignmentStartDateWithWeekCountLegacy,
    startDateWithWeekCount: assignmentStartDateWithWeekCount,
    time: assignmentTime,
    fullDayDate: assignmentFullDayDate,
    positions: assignmentPositions,
    weekCount,
    expectedHours: assignmentExpectedHours,
  },
  location: {
    cityStateZip,
    cityState,
  },
  specialty: {
    formatSpecialties,
  },
  payout: {
    getPayoutStatus,
  },
};

export default DisplayHelpers;
